/** INR + time formatters. The whole app shows money the way the client reads it. */
import { CURRENCY_META, type BillingCurrency } from "@talenthop/shared";

const inrFmt = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

/** ₹27,000 — no paise, en-IN grouping (lakh/crore). */
export function inr(n: number): string {
  return inrFmt.format(Math.round(n));
}

/** Compact INR for headline tiles: ₹3.67L, ₹1.2Cr. */
export function inrCompact(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  return inr(n);
}

/**
 * An amount in a billing currency's MINOR units (0031 — how the gateway and the org rollups carry
 * money), shown the way that org reads it: "₹12,300", "$140", "£95". Multi-currency figures roll
 * up onto one axis only via the ₹-equivalent the API computes with FX_TO_INR — never here.
 */
export function currencyMinor(minor: number, currency: BillingCurrency): string {
  const meta = CURRENCY_META[currency] ?? CURRENCY_META.INR;
  return new Intl.NumberFormat(meta.locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(minor / meta.minor);
}

/** 37 hrs / 194 hrs. */
export function hours(h: number): string {
  const rounded = Math.round(h * 10) / 10;
  return `${rounded} ${rounded === 1 ? "hr" : "hrs"}`;
}

/*
 * Dual-currency display.
 *
 * Standing requirement: every money figure must show BOTH ₹ and US$. The app's
 * canonical amounts are always stored/computed in INR; the USD figure is a
 * DISPLAY-ONLY convenience conversion at a single fixed rate — it is never a
 * transactional/settlement value and must not be used for billing math.
 *
 * There is exactly ONE rate constant below. If the FX assumption changes, update
 * USD_PER_INR here and the marketing site's ~₹86/$1 note (apps/site/pricing.html)
 * so the two stay in sync.
 */

/** Display-only FX; marketing site apps/site/pricing.html uses ~₹86/$1. UPDATE if the rate changes. */
export const USD_PER_INR = 1 / 86;

const usdFmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

/** $1,234 — no cents, en-US grouping. Handles 0 and negatives. */
export function usd(inrAmount: number): string {
  return usdFmt.format(Math.round(inrAmount * USD_PER_INR));
}

/**
 * Primary dual-display helper: "₹1,00,000 · $1,163".
 * Use this at call sites so both currencies stay together.
 */
export function inrUsd(inrAmount: number): string {
  return `${inr(inrAmount)} · ${usd(inrAmount)}`;
}

/** Compact USD for headline tiles: $1.2k, $1.2M — mirrors inrCompact. Handles 0 and negatives. */
export function usdCompact(inrAmount: number): string {
  const dollars = inrAmount * USD_PER_INR;
  const abs = Math.abs(dollars);
  if (abs >= 1e6) return `$${(dollars / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(dollars / 1e3).toFixed(1)}k`;
  return usd(inrAmount);
}

/**
 * A short activity-date label: "Now" / "Today" / "Yesterday" / else the actual date. For list rows
 * that need to say WHEN something last happened without a full timestamp (candidates, interviews,
 * …). Inbox.tsx has its own, older minutes/hours-ago formatter (`when()`) built for a message
 * list; this is a deliberately separate, coarser convention rather than a variant bolted onto it.
 */
export function relativeActivityDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";

  const now = new Date();
  if (now.getTime() - d.getTime() < 60_000) return "Now";

  const startOfDay = (x: Date): number => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);

  if (dayDiff === 0) return "Today";
  if (dayDiff === 1) return "Yesterday";
  return d.toLocaleDateString();
}
